// KubiChatbot.jsx - kNine AI Floating Chatbot
// Loads org/user context on open to personalise quick actions and suggestions

import React, { useState, useEffect, useRef, useCallback } from 'react';

// Normalize base URL — strip trailing /api if present so all endpoints use /api/ prefix
const _rawBase = process.env.REACT_APP_API_URL || 'https://kubi-backend-production.up.railway.app';
const API_BASE = _rawBase.endsWith('/api') ? _rawBase.slice(0, -4) : _rawBase;

// API helper with auth
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
};

// Inject animations
const injectChatStyles = () => {
  if (typeof document === 'undefined' || document.getElementById('kubi-chat-styles')) return;
  const styles = document.createElement('style');
  styles.id = 'kubi-chat-styles';
  styles.textContent = `
    @keyframes kubiSlideUp {
      from { opacity: 0; transform: translateY(20px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes kubiPulse {
      0%, 100% { transform: scale(1); box-shadow: 0 4px 20px rgba(10, 102, 194, 0.3); }
      50% { transform: scale(1.05); box-shadow: 0 8px 30px rgba(10, 102, 194, 0.5); }
    }
    @keyframes kubiBounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
    }
    @keyframes kubiSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes kubiFadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes kubiGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
      50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6); }
    }
    .kubi-chat-slide-up { animation: kubiSlideUp 0.3s ease-out forwards; }
    .kubi-chat-bounce { animation: kubiBounce 1s ease-in-out infinite; }
    .kubi-chat-spin { animation: kubiSpin 1s linear infinite; }
    .kubi-chat-fade { animation: kubiFadeIn 0.3s ease-out forwards; }
    .kubi-chat-glow { animation: kubiGlow 2s ease-in-out infinite; }
    .kubi-typing-dot { animation: kubiBounce 1.4s ease-in-out infinite; }
    .kubi-typing-dot:nth-child(1) { animation-delay: 0s; }
    .kubi-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .kubi-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    .kubi-chat-scrollbar::-webkit-scrollbar { width: 6px; }
    .kubi-chat-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .kubi-chat-scrollbar::-webkit-scrollbar-thumb { background: rgba(100,100,100,0.3); border-radius: 3px; }
    .kubi-chat-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,100,100,0.5); }
  `;
  document.head.appendChild(styles);
};

// Tool Result Cards — rendered below assistant message when tool calls return data
const ToolResultCards = ({ toolResults, isDark }) => {
  if (!toolResults || toolResults.length === 0) return null;

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toolResults.map((tr, i) => {
        const toolName = tr.tool || tr.toolName;
        const result = tr.result;
        if (!result || result.error) return null;

        if (toolName === 'query_my_candidates') {
          const candidates = result.candidates || [];
          if (!candidates.length) return null;
          return (
            <div key={i} style={{ borderRadius: 12, border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.2)'}`, background: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.08)', fontSize: 11, fontWeight: 700, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>👤</span> {candidates.length} Candidate{candidates.length !== 1 ? 's' : ''} from Your Database
              </div>
              {candidates.slice(0, 5).map((c, ci) => (
                <div key={ci} style={{ padding: '8px 12px', borderTop: ci === 0 ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {(c.name || c.firstName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[c.title, c.company || c.currentCompany].filter(Boolean).join(' · ') || c.location || ''}
                    </div>
                  </div>
                  {c.lastActivity && (
                    <div style={{ fontSize: 10, color: isDark ? '#64748b' : '#94a3b8', flexShrink: 0 }}>
                      {new Date(c.lastActivity).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
              {candidates.length > 5 && (
                <div style={{ padding: '6px 12px', fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', textAlign: 'center', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` }}>
                  +{candidates.length - 5} more
                </div>
              )}
            </div>
          );
        }

        if (toolName === 'query_my_positions') {
          const positions = result.positions || [];
          if (!positions.length) return null;
          return (
            <div key={i} style={{ borderRadius: 12, border: `1px solid ${isDark ? 'rgba(16,185,129,0.3)' : 'rgba(16,185,129,0.2)'}`, background: isDark ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)', fontSize: 11, fontWeight: 700, color: '#10b981', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>📋</span> {positions.length} Open Position{positions.length !== 1 ? 's' : ''}
              </div>
              {positions.slice(0, 5).map((p, pi) => (
                <div key={pi} style={{ padding: '8px 12px', borderTop: pi === 0 ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{p.title || p.jobTitle || 'Untitled'}</div>
                  <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {p.company && <span>{p.company}</span>}
                    {p.location && <span>📍 {p.location}</span>}
                    {p.openedDate && <span>Opened {new Date(p.openedDate).toLocaleDateString()}</span>}
                    {p.candidateCount != null && <span style={{ color: p.candidateCount === 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>{p.candidateCount} candidates</span>}
                  </div>
                </div>
              ))}
              {positions.length > 5 && (
                <div style={{ padding: '6px 12px', fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', textAlign: 'center', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` }}>
                  +{positions.length - 5} more
                </div>
              )}
            </div>
          );
        }

        if (toolName === 'query_my_clients') {
          const companies = result.companies || [];
          if (!companies.length) return null;
          return (
            <div key={i} style={{ borderRadius: 12, border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.2)'}`, background: isDark ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)', fontSize: 11, fontWeight: 700, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>🏢</span> {companies.length} Client{companies.length !== 1 ? 's' : ''} / Companies
              </div>
              {companies.slice(0, 5).map((c, ci) => (
                <div key={ci} style={{ padding: '8px 12px', borderTop: ci === 0 ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{c.name || c.companyName || 'Unknown'}</div>
                  <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {c.industry && <span>{c.industry}</span>}
                    {c.location && <span>📍 {c.location}</span>}
                    {c.openPositions != null && <span style={{ fontWeight: 600, color: c.openPositions > 0 ? '#f59e0b' : 'inherit' }}>{c.openPositions} open role{c.openPositions !== 1 ? 's' : ''}</span>}
                    {c.lastSearch && <span>Last searched {new Date(c.lastSearch).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))}
              {companies.length > 5 && (
                <div style={{ padding: '6px 12px', fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', textAlign: 'center', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` }}>
                  +{companies.length - 5} more
                </div>
              )}
            </div>
          );
        }

        if (toolName === 'search_knine') {
          const candidates = result.candidates || result.results || [];
          const total = result.totalFound || result.total || candidates.length;
          return (
            <div key={i} style={{ borderRadius: 12, border: `1px solid ${isDark ? 'rgba(139,92,246,0.35)' : 'rgba(139,92,246,0.25)'}`, background: isDark ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)', fontSize: 11, fontWeight: 700, color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span>🔍</span> Roxi Search · {total.toLocaleString()} results</span>
                <span style={{ background: 'rgba(139,92,246,0.2)', padding: '2px 6px', borderRadius: 6, fontSize: 10 }}>1 credit</span>
              </div>
              {candidates.slice(0, 4).map((c, ci) => (
                <div key={ci} style={{ padding: '8px 12px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 7, background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                    {(c.name || c.firstName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[c.jobTitle || c.title, c.company || c.employer].filter(Boolean).join(' · ') || c.location || ''}
                    </div>
                  </div>
                </div>
              ))}
              {total > 4 && (
                <div style={{ padding: '8px 12px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`, textAlign: 'center' }}>
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('knine-open-roxi', { detail: { query: result.query || result.searchQuery } }))}
                    style={{ fontSize: 12, fontWeight: 600, color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    View all {total.toLocaleString()} results in Roxi →
                  </button>
                </div>
              )}
            </div>
          );
        }

        if (toolName === 'search_bd_companies') {
          const companies = result.companies || result.results || [];
          const total = result.totalFound || result.total || companies.length;
          return (
            <div key={i} style={{ borderRadius: 12, border: `1px solid ${isDark ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.25)'}`, background: isDark ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.08)', fontSize: 11, fontWeight: 700, color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span>🏢</span> Cooper BD Search · {total.toLocaleString()} companies</span>
                <span style={{ background: 'rgba(16,185,129,0.2)', padding: '2px 6px', borderRadius: 6, fontSize: 10 }}>1 credit</span>
              </div>
              {companies.slice(0, 4).map((c, ci) => (
                <div key={ci} style={{ padding: '8px 12px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{c.name || c.companyName || 'Unknown'}</div>
                  <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {c.industry && <span>{c.industry}</span>}
                    {c.location && <span>📍 {c.location}</span>}
                    {c.employeeCount && <span>{c.employeeCount.toLocaleString()} employees</span>}
                  </div>
                </div>
              ))}
              {total > 4 && (
                <div style={{ padding: '8px 12px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`, textAlign: 'center' }}>
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('knine-open-cooper', { detail: { query: result.query } }))}
                    style={{ fontSize: 12, fontWeight: 600, color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    View all {total.toLocaleString()} companies in Cooper →
                  </button>
                </div>
              )}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

// Credit usage pill — shown after a message that used credits
const CreditUsedPill = ({ creditsUsed, newCreditBalance, isDark }) => {
  if (!creditsUsed || creditsUsed === 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: -8, marginBottom: 12, paddingLeft: 42 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 10, background: isDark ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.08)', border: `1px solid ${isDark ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.15)'}`, fontSize: 11, color: '#8b5cf6', fontWeight: 600 }}>
        <span>⚡</span>
        {creditsUsed} credit{creditsUsed !== 1 ? 's' : ''} used
        {newCreditBalance != null && <span style={{ fontWeight: 400, color: isDark ? '#94a3b8' : '#64748b' }}>· {newCreditBalance} remaining</span>}
      </div>
    </div>
  );
};

// Icons
const Icons = {
  sparkles: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
      <path d="M20 3v4"/><path d="M22 5h-4"/>
    </svg>
  ),
  send: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>
    </svg>
  ),
  x: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
    </svg>
  ),
  minimize: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h16"/>
    </svg>
  ),
  brain: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/>
    </svg>
  ),
  loader: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
  ),
  lightbulb: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
      <path d="M9 18h6"/><path d="M10 22h4"/>
    </svg>
  ),
  zap: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  target: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  arrowLeft: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
    </svg>
  ),
  trash: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
    </svg>
  ),
  database: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  ),
  link: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  ),
};

// Markdown renderer — handles **bold**, ### headers, - bullets, numbered lists, `code`
const MarkdownText = ({ text, color }) => {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div style={{ lineHeight: 1.65 }}>
      {lines.map((line, i) => {
        // Header
        if (/^###\s/.test(line)) return <div key={i} style={{ fontWeight: 700, fontSize: 13, marginTop: 10, marginBottom: 4, color: color || 'inherit' }}>{line.replace(/^###\s/, '')}</div>;
        if (/^##\s/.test(line)) return <div key={i} style={{ fontWeight: 700, fontSize: 14, marginTop: 12, marginBottom: 4, color: color || 'inherit' }}>{line.replace(/^##\s/, '')}</div>;
        if (/^#\s/.test(line)) return <div key={i} style={{ fontWeight: 800, fontSize: 15, marginTop: 14, marginBottom: 6, color: color || 'inherit' }}>{line.replace(/^#\s/, '')}</div>;
        // Bullet
        if (/^[-*]\s/.test(line)) {
          const content = line.replace(/^[-*]\s/, '');
          return <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3, paddingLeft: 4 }}><span style={{ color: '#8b5cf6', fontWeight: 700, flexShrink: 0 }}>•</span><span>{renderInline(content)}</span></div>;
        }
        // Numbered list
        if (/^\d+\.\s/.test(line)) {
          const num = line.match(/^(\d+)\./)[1];
          const content = line.replace(/^\d+\.\s/, '');
          return <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3, paddingLeft: 4 }}><span style={{ color: '#8b5cf6', fontWeight: 700, flexShrink: 0, minWidth: 16 }}>{num}.</span><span>{renderInline(content)}</span></div>;
        }
        // Empty line = spacer
        if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
        // Normal paragraph
        return <div key={i} style={{ marginBottom: 2 }}>{renderInline(line)}</div>;
      })}
    </div>
  );
};

// Render inline markdown: **bold**, *italic*, `code`
const renderInline = (text) => {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`(.+?)`/);
    const italicMatch = remaining.match(/\*(.+?)\*/);
    let earliest = null;
    if (boldMatch && (!earliest || boldMatch.index < earliest.index)) earliest = { match: boldMatch, type: 'bold' };
    if (codeMatch && (!earliest || codeMatch.index < earliest.index)) earliest = { match: codeMatch, type: 'code' };
    if (italicMatch && italicMatch[0].indexOf('**') === -1 && (!earliest || italicMatch.index < earliest.index)) earliest = { match: italicMatch, type: 'italic' };
    if (!earliest) { parts.push(<span key={key++}>{remaining}</span>); break; }
    if (earliest.match.index > 0) parts.push(<span key={key++}>{remaining.slice(0, earliest.match.index)}</span>);
    if (earliest.type === 'bold') parts.push(<strong key={key++}>{earliest.match[1]}</strong>);
    else if (earliest.type === 'code') parts.push(<code key={key++} style={{ background: 'rgba(139,92,246,0.15)', padding: '1px 4px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>{earliest.match[1]}</code>);
    else if (earliest.type === 'italic') parts.push(<em key={key++}>{earliest.match[1]}</em>);
    remaining = remaining.slice(earliest.match.index + earliest.match[0].length);
  }
  return parts;
};

// Chat Message Component
const ChatMessage = ({ message, isUser, isDark }) => (
  <div className="kubi-chat-fade" style={{
    display: 'flex',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    marginBottom: 16,
  }}>
    {!isUser && (
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
        flexShrink: 0,
        color: '#fff',
      }}>
        {Icons.brain}
      </div>
    )}
    <div style={{
      maxWidth: '78%',
      padding: '12px 16px',
      borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
      background: isUser
        ? 'linear-gradient(135deg, #0a66c2, #0856a8)'
        : (isDark ? '#2d3748' : '#f1f5f9'),
      color: isUser ? '#fff' : (isDark ? '#e2e8f0' : '#1e293b'),
      fontSize: 14,
      lineHeight: 1.6,
      boxShadow: isUser ? '0 4px 12px rgba(10, 102, 194, 0.3)' : 'none',
    }}>
      {isUser
        ? <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span>
        : <MarkdownText text={message.content} color={isDark ? '#e2e8f0' : '#1e293b'} />
      }
    </div>
  </div>
);

// Typing Indicator
const TypingIndicator = ({ isDark }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  }}>
    <div style={{
      width: 32,
      height: 32,
      borderRadius: 8,
      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
    }}>
      {Icons.brain}
    </div>
    <div style={{
      padding: '14px 18px',
      borderRadius: '16px 16px 16px 4px',
      background: isDark ? '#2d3748' : '#f1f5f9',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    }}>
      {[0, 1, 2].map(i => (
        <div key={i} className="kubi-typing-dot" style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          background: isDark ? '#64748b' : '#94a3b8',
        }} />
      ))}
    </div>
  </div>
);

// Quick Action Button
const QuickAction = ({ icon, text, onClick, isDark }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 14px',
      borderRadius: 12,
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
      color: isDark ? '#e2e8f0' : '#334155',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.background = isDark ? 'rgba(139, 92, 246, 0.15)' : 'rgba(10, 102, 194, 0.08)';
      e.currentTarget.style.borderColor = '#8b5cf6';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)';
      e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)';
    }}
  >
    <span style={{ color: '#8b5cf6' }}>{icon}</span>
    {text}
  </button>
);

// Fallback responses when API is unavailable
const getFallbackResponse = (message) => {
  const lowerMsg = message.toLowerCase();

  if (lowerMsg.includes('priorit') || lowerMsg.includes('today') || lowerMsg.includes('focus')) {
    return `Here are some priorities to consider for today:

**High Priority:**
1. Follow up with any candidates who had interviews this week
2. Review and respond to pending candidate submissions
3. Check for any urgent position updates from clients

**Medium Priority:**
1. Source new candidates for your open positions
2. Update candidate statuses in your ATS
3. Schedule upcoming interviews

Would you like help with any specific task?`;
  }

  if (lowerMsg.includes('pipeline') || lowerMsg.includes('health')) {
    return `To analyze your pipeline health, I'd recommend checking:

**Key Metrics to Review:**
- Active candidates per open position
- Time-to-fill for recent placements
- Candidate response rates
- Interview-to-offer ratios

**Quick Actions:**
1. Review candidates who haven't been contacted in 7+ days
2. Check positions with fewer than 3 active candidates
3. Follow up on pending interview feedback

Connect your ATS integration to see real-time pipeline analytics!`;
  }

  if (lowerMsg.includes('search') || lowerMsg.includes('tip') || lowerMsg.includes('candidate')) {
    return `Here are some search tips to find better candidates:

**Boolean Search Tips:**
- Use quotes for exact phrases: "software engineer"
- Combine with AND/OR: Java AND (Spring OR Hibernate)
- Exclude with NOT: Developer NOT Junior

**Pro Tips:**
1. Try alternative job titles (e.g., "Software Developer" vs "Software Engineer")
2. Search for specific skills rather than just titles
3. Include company names of competitors
4. Use location radius for hybrid roles

Would you like help building a specific search query?`;
  }

  if (lowerMsg.includes('quick win') || lowerMsg.includes('fast')) {
    return `Here are some quick wins you can achieve today:

**5-Minute Tasks:**
1. Send a quick check-in to your top 3 candidates
2. Update statuses for candidates awaiting feedback
3. Review and respond to any InMails

**15-Minute Tasks:**
1. Review one candidate's profile and provide feedback
2. Draft an outreach template for a new position
3. Connect with 5 relevant professionals on LinkedIn

**30-Minute Tasks:**
1. Complete a thorough candidate sourcing session
2. Prep interview questions for upcoming screens
3. Sync with hiring manager on position requirements`;
  }

  return `I'm here to help with your recruiting needs! Here's what I can assist with:

**Candidate Sourcing:**
- Search strategies and Boolean tips
- Alternative title suggestions
- Market insights

**Pipeline Management:**
- Prioritize follow-ups
- Identify stale candidates
- Track key metrics

**Communication:**
- Draft outreach messages
- Interview prep
- Candidate engagement

What would you like to work on?`;
};

// Insight Card component — shown on home screen before any messages
const InsightCard = ({ insight, isDark, onAction }) => {
  const colorMap = {
    stale_position: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
    search_fix: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
    bd_opportunity: { bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.3)', text: '#10b981' },
    pipeline_alert: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
    quick_win: { bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.3)', text: '#6366f1' },
    market_insight: { bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.3)', text: '#8b5cf6' },
  };
  const colors = colorMap[insight.type] || colorMap.quick_win;
  return (
    <div style={{ padding: '10px 12px', borderRadius: 12, background: colors.bg, border: `1px solid ${colors.border}`, marginBottom: 8, cursor: 'pointer' }}
      onClick={() => onAction(insight)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{insight.emoji || '💡'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.text, marginBottom: 3 }}>{insight.title}</div>
          <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>{insight.insight}</div>
          {insight.action && (
            <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: colors.text, display: 'flex', alignItems: 'center', gap: 4 }}>
              → {insight.action}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Chatbot Component
const KubiChatbot = ({ isOpen, onClose, isDark }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [user, setUser] = useState(null);
  const [chatContext, setChatContext] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    injectChatStyles();
    // Get user info
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      setUser(userData);
    } catch {}
  }, []);

  // Load context and insights when chat opens
  useEffect(() => {
    if (!isOpen) return;
    // Load context (fast)
    apiCall('/api/ai/kubi/context')
      .then(ctx => setChatContext(ctx))
      .catch(() => {});
    // Load proactive insights (may take a moment — Claude call)
    setInsightsLoading(true);
    apiCall('/api/ai/kubi/insights')
      .then(data => setInsights(data))
      .catch(() => setInsights(null))
      .finally(() => setInsightsLoading(false));
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Clear conversation
  const clearConversation = () => {
    setMessages([]);
    setConversationId(null);
  };

  // Send message
  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || loading) return;

    // Tour intent detection — trigger the onboarding tour from chat instantly
    const tourTriggers = ['show me around', 'take a tour', 'give me a tour', 'tour', 'walkthrough', 'walk me through', 'onboarding', 'how does this work', 'getting started'];
    if (tourTriggers.some(t => text.toLowerCase().includes(t))) {
      setMessages(prev => [...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: "Starting the tour now — I'll walk you through Roxi, Cooper, and everything on your trial. 🚀" },
      ]);
      setInput('');
      // Dispatch a CustomEvent — MainLayout responds immediately, no polling lag
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('knine-show-tour'));
        onClose();
      }, 600);
      return;
    }

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await apiCall('/api/ai/kubi/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: text,
          conversationId,
        }),
      });

      if (response.conversationId) setConversationId(response.conversationId);
      const aiText = response.response || response.message;
      if (!aiText) throw new Error('Empty response from AI');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiText,
        toolResults: response.toolResults || [],
        creditsUsed: response.creditsUsed || 0,
        newCreditBalance: response.newCreditBalance,
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I ran into a temporary issue connecting to my AI engine. Please try again — if the problem persists, check that your API key is configured in settings.\n\n*Error: ${err.message || 'connection failed'}*`
      }]);
    }

    setLoading(false);
  }, [loading, conversationId]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const triggerTour = () => {
    window.dispatchEvent(new CustomEvent('knine-show-tour'));
    onClose();
  };

  // Handle clicking an insight card — turn it into a targeted prompt
  const handleInsightAction = (insight) => {
    let prompt;
    if (insight.promptSuggestion) {
      prompt = `Search for: ${insight.promptSuggestion}. ${insight.insight} What should I know about this search and how can I find the best candidates?`;
    } else if (insight.type === 'stale_position') {
      prompt = `${insight.insight} Help me create kNine searches for these positions and suggest alternative job titles to expand my candidate pool.`;
    } else if (insight.type === 'search_fix') {
      prompt = `${insight.insight} Suggest alternative search strategies, different job titles, and search queries I should try instead.`;
    } else if (insight.type === 'bd_opportunity') {
      prompt = `${insight.insight} Help me develop a BD strategy and outreach approach for these companies.`;
    } else {
      prompt = `${insight.insight} ${insight.action}`;
    }
    sendMessage(prompt);
  };

  // Build quick actions dynamically from context + insights
  const quickActions = (() => {
    const ctx = chatContext;
    const hasATS = ctx?.ats?.hasConnectedATS ?? true;
    const hasSearches = ctx?.activity?.hasSearchHistory;
    const zeroResults = ctx?.activity?.zeroResultSearchesThisWeek || 0;
    const topTitle = ctx?.searchPatterns?.topTitles?.[0];
    const recentSearch = ctx?.searchPatterns?.recentSearches?.[0];
    const actions = [];

    actions.push({ icon: Icons.target, text: "Today's priorities", prompt: "What should I focus on today based on my data?" });

    if (ctx === null || hasATS) {
      actions.push({ icon: Icons.users, text: "Pipeline review", prompt: "Analyze my current pipeline — who needs follow-up and which positions are going cold?" });
    } else {
      actions.push({ icon: Icons.database, text: "Connect ATS", prompt: "How do I connect my ATS database to kNine?" });
    }

    if (zeroResults > 0) {
      actions.push({ icon: Icons.lightbulb, text: `Expand ${zeroResults} search${zeroResults > 1 ? 'es' : ''}`, prompt: `I have ${zeroResults} search${zeroResults > 1 ? 'es' : ''} with narrow results. Suggest broader queries, alternative job titles, and strategies to surface more strong candidates.` });
    } else if (topTitle) {
      actions.push({ icon: Icons.lightbulb, text: `Source ${topTitle}`, prompt: `Give me advanced sourcing strategies for finding ${topTitle} candidates — alternative titles, target companies, key skills, and specific kNine search queries to run.` });
    } else {
      actions.push({ icon: Icons.lightbulb, text: "Search strategy", prompt: "Help me build a more effective candidate search strategy based on what I've been searching for." });
    }

    if (hasSearches) {
      actions.push({ icon: Icons.zap, text: "BD opportunities", prompt: "Based on my recent searches and the roles I'm filling, what are the best business development opportunities I should pursue right now? Which companies should I be targeting?" });
    } else {
      actions.push({ icon: Icons.zap, text: "Quick wins", prompt: "What are some quick wins I can do right now to improve my recruiting results?" });
    }

    if (recentSearch) {
      actions.push({ icon: Icons.sparkles, text: `Refine: "${(recentSearch.query || '').slice(0, 20)}${recentSearch.query?.length > 20 ? '…' : ''}"`, prompt: `My recent search "${recentSearch.query}" returned ${recentSearch.results} results. ${recentSearch.results === 0 ? 'Suggest broader search terms and alternative approaches to surface strong candidates for this role.' : 'How can I refine this to find even better candidates?'}` });
    } else {
      actions.push({ icon: Icons.sparkles, text: "Take a tour", onClick: triggerTour });
    }
    return actions;
  })();

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(2px)',
          zIndex: 9998,
        }}
      />

      {/* Chat Modal */}
      <div
        className="kubi-chat-slide-up"
        style={{
          position: 'fixed',
          bottom: 100,
          right: 24,
          width: 420,
          height: 600,
          maxHeight: 'calc(100vh - 140px)',
          background: isDark ? '#1a202c' : '#ffffff',
          borderRadius: 20,
          boxShadow: isDark
            ? '0 25px 80px rgba(0,0,0,0.5), 0 0 40px rgba(139, 92, 246, 0.15)'
            : '0 25px 80px rgba(0,0,0,0.15)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 9999,
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          {messages.length > 0 && (
            <button
              onClick={clearConversation}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: 'none',
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                color: isDark ? '#94a3b8' : '#64748b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              title="New conversation"
            >
              {Icons.arrowLeft}
            </button>
          )}
          <div className="kubi-chat-glow" style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}>
            {Icons.sparkles}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              color: isDark ? '#f8fafc' : '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              kNine AI
              <span style={{
                padding: '2px 8px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}>
                Online
              </span>
            </h3>
            <p style={{
              margin: '2px 0 0',
              fontSize: 12,
              color: isDark ? '#94a3b8' : '#64748b'
            }}>
              Your intelligent recruiting assistant
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: 'none',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              color: isDark ? '#94a3b8' : '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.1)'}
            onMouseOut={(e) => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
          >
            {Icons.x}
          </button>
        </div>

        {/* Messages */}
        <div
          ref={chatContainerRef}
          className="kubi-chat-scrollbar"
          style={{
            flex: 1,
            padding: 20,
            overflowY: 'auto',
            background: isDark ? '#1a202c' : '#fff',
          }}
        >
          {messages.length === 0 ? (
            <div style={{ padding: '16px 0 4px' }}>
              {/* Greeting */}
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(99,102,241,0.08))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 10px', color: '#8b5cf6',
                }}>{Icons.brain}</div>
                <h4 style={{ margin: '0 0 3px', fontSize: 16, fontWeight: 700, color: isDark ? '#f8fafc' : '#1e293b' }}>
                  Hi{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
                </h4>
                <p style={{ margin: 0, fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                  {chatContext?.activity?.recentSearchCount > 0
                    ? `${chatContext.activity.recentSearchCount} searches this month · Let's find more candidates`
                    : 'Your recruiting & BD intelligence assistant'}
                </p>
              </div>

              {/* ATS nudge */}
              {chatContext && !chatContext.ats?.hasConnectedATS && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 12, background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', marginBottom: 10, cursor: 'pointer' }}
                  onClick={() => sendMessage("How do I connect my ATS database to kNine?")}>
                  <span style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }}>{Icons.database}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#f59e0b' }}>Connect your ATS for full intelligence</div>
                    <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2 }}>Unlocks pipeline analysis, stale position alerts, and BD signals.</div>
                  </div>
                </div>
              )}

              {/* Proactive AI Insights */}
              {insightsLoading && (
                <div style={{ padding: '10px 12px', borderRadius: 12, background: isDark ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.04)', border: `1px solid ${isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.1)'}`, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="kubi-chat-spin" style={{ display: 'inline-block' }}>{Icons.loader}</span>
                    Analyzing your data…
                  </div>
                </div>
              )}

              {!insightsLoading && insights?.insights?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Your Briefing — tap to act
                  </div>
                  {insights.insights.slice(0, 4).map((insight, i) => (
                    <InsightCard key={i} insight={insight} isDark={isDark} onAction={handleInsightAction} />
                  ))}
                  {insights.dataPoints && (
                    <div style={{ fontSize: 10, color: isDark ? '#475569' : '#94a3b8', marginTop: 2, textAlign: 'right' }}>
                      {insights.dataPoints.openPositions || 0} open positions · {insights.dataPoints.recentSearches || 0} searches analyzed
                    </div>
                  )}
                </div>
              )}

              {/* Quick Actions */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Quick Actions
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {quickActions.map((action, idx) => (
                    <QuickAction key={idx} icon={action.icon} text={action.text}
                      onClick={action.onClick || (() => sendMessage(action.prompt))} isDark={isDark} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <React.Fragment key={idx}>
                  <ChatMessage
                    message={msg}
                    isUser={msg.role === 'user'}
                    isDark={isDark}
                  />
                  {msg.role === 'assistant' && msg.toolResults?.length > 0 && (
                    <div style={{ paddingLeft: 42, marginTop: -8, marginBottom: 16 }}>
                      <ToolResultCards toolResults={msg.toolResults} isDark={isDark} />
                    </div>
                  )}
                  {msg.role === 'assistant' && msg.creditsUsed > 0 && (
                    <CreditUsedPill creditsUsed={msg.creditsUsed} newCreditBalance={msg.newCreditBalance} isDark={isDark} />
                  )}
                </React.Fragment>
              ))}
              {loading && <TypingIndicator isDark={isDark} />}
            </>
          )}
        </div>

        {/* Input */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? '#1e293b' : '#fafbfc',
        }}>
          <div style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-end',
          }}>
            <div style={{
              flex: 1,
              position: 'relative',
            }}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask kNine AI anything..."
                rows={1}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: 14,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                  background: isDark ? '#0f172a' : '#fff',
                  color: isDark ? '#f8fafc' : '#1e293b',
                  fontSize: 14,
                  lineHeight: 1.5,
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#8b5cf6';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                border: 'none',
                background: input.trim() && !loading
                  ? 'linear-gradient(135deg, #8b5cf6, #6366f1)'
                  : (isDark ? '#2d3748' : '#e2e8f0'),
                color: input.trim() && !loading ? '#fff' : (isDark ? '#64748b' : '#94a3b8'),
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
            >
              {loading ? (
                <span className="kubi-chat-spin">{Icons.loader}</span>
              ) : (
                Icons.send
              )}
            </button>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginTop: 12,
            fontSize: 11,
            color: isDark ? '#64748b' : '#94a3b8',
          }}>
            <span style={{ color: '#8b5cf6', display: 'flex' }}>{Icons.sparkles}</span>
            Powered by kNine AI
          </div>
        </div>
      </div>
    </>
  );
};

// Floating Chat Button — right-edge pull tab, fully collapsed when idle
export const KubiChatButton = ({ onClick, isDark }) => {
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    injectChatStyles();
  }, []);

  // Collapsed: only a 6px sliver peeks out from the right edge
  // Hovered: slides fully out to show icon + label
  const COLLAPSED_WIDTH = 44;
  const EXPANDED_WIDTH = 136;
  const SLIVER = 6; // px visible when collapsed

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="Open kNine AI"
      style={{
        position: 'fixed',
        top: '58%',
        right: isHovered ? 0 : -(COLLAPSED_WIDTH - SLIVER),
        transform: 'translateY(-50%)',
        width: isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        height: 44,
        borderRadius: '10px 0 0 10px',
        border: 'none',
        background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        paddingLeft: 12,
        paddingRight: isHovered ? 16 : 0,
        boxShadow: isHovered
          ? '-6px 0 24px rgba(139, 92, 246, 0.5)'
          : '-2px 0 8px rgba(99, 102, 241, 0.3)',
        transition: 'right 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s',
        zIndex: 9997,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        transform: isHovered ? 'rotate(-10deg) scale(1.15)' : 'none',
        transition: 'transform 0.25s',
      }}>
        {Icons.sparkles}
      </span>
      <span style={{
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.2,
        opacity: isHovered ? 1 : 0,
        maxWidth: isHovered ? 90 : 0,
        overflow: 'hidden',
        transition: 'opacity 0.15s 0.1s, max-width 0.25s',
      }}>
        Ask kNine
      </span>
    </button>
  );
};

export default KubiChatbot;
